// Editorial scroll-tied hero animation:
//   reveal=0  wordmark big & visible, image hidden, caption hidden
//   reveal=1  wordmark shrinking + moving up + fading, image fading in
//   reveal=2  wordmark gone, image visible, caption fades in
//   reveal=3  CTA appears, scroll hint hides
//
// Wordmark transform/opacity is GSAP-controlled (continuous).
// Image/caption/CTA opacity is driven by data-reveal stages from CSS.

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export function initHero(): void {
  const hero = document.querySelector<HTMLElement>('[data-hero]')
  if (!hero) return

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const masthead = hero.querySelector<HTMLElement>('.hero__masthead')

  // After first scroll past the hero, hide the scroll hint.
  const onScroll = (): void => {
    if (window.scrollY > 24) {
      hero.dataset.scrolled = 'true'
      window.removeEventListener('scroll', onScroll)
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true })

  if (reduced) {
    hero.dataset.reveal = '3'
    return
  }

  hero.dataset.reveal = '0'

  const heroVideo = hero.querySelector<HTMLVideoElement>('[data-hero-video]')
  let videoStarted = false
  let heroCanvas: HTMLCanvasElement | null = null

  if (heroVideo) {
    const W = 600, H = 450
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    canvas.className = 'hero__video-canvas'
    heroVideo.parentElement!.appendChild(canvas)
    heroCanvas = canvas
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!
    // Luma threshold — pixels with avg brightness above this become transparent.
    // 190 catches warm gray backgrounds without cutting into dark frame detail.
    const THRESH = 190
    let raf = 0
    function drawFrame() {
      if (!heroVideo!.paused && !heroVideo!.ended) {
        ctx.drawImage(heroVideo!, 0, 0, W, H)
        const id = ctx.getImageData(0, 0, W, H)
        const d = id.data
        for (let i = 0; i < d.length; i += 4) {
          if ((d[i] + d[i + 1] + d[i + 2]) / 3 > THRESH) d[i + 3] = 0
        }
        ctx.putImageData(id, 0, 0)
      }
      raf = requestAnimationFrame(drawFrame)
    }
    heroVideo.addEventListener('play', drawFrame)
    heroVideo.addEventListener('pause', () => cancelAnimationFrame(raf))
    heroVideo.addEventListener('ended', () => cancelAnimationFrame(raf))
  }

  ScrollTrigger.create({
    trigger: hero,
    start: 'top top',
    end: '+=100%',
    scrub: 0.5,
    pin: true,
    anticipatePin: 1,
    onUpdate: (self) => {
      const p = self.progress

      // Reveal stages drive image/caption opacity in CSS.
      if (p < 0.2) hero.dataset.reveal = '0'
      else if (p < 0.5) hero.dataset.reveal = '1'
      else if (p < 0.8) hero.dataset.reveal = '2'
      else hero.dataset.reveal = '3'

      if (p >= 0.5 && heroVideo && !videoStarted) {
        videoStarted = true
        heroVideo.playbackRate = 0.5
        heroVideo.play().catch(() => {})
      }
      if (heroCanvas) heroCanvas.style.opacity = ''  // driven by CSS data-reveal

      // Wordmark: scale 1 → 0.35, translate up to -10vh, opacity 1 → 0.
      // It exits cleanly so the image takes the focal point mid-scroll.
      if (masthead) {
        const scale = Math.max(0.35, 1 - p * 0.95)
        const yPct = -p * 10
        const op = Math.max(0, 1 - p * 1.6)
        gsap.set(masthead, {
          scale,
          y: `${yPct}vh`,
          opacity: op,
          transformOrigin: '50% 50%',
        })
      }
    },
  })

  // Subtle desktop pointer tilt on the product image.
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return
  const product = hero.querySelector<HTMLElement>('.hero__product')
  if (!product) return
  let raf = 0
  hero.addEventListener('mousemove', (e) => {
    if (raf) cancelAnimationFrame(raf)
    raf = requestAnimationFrame(() => {
      const rect = hero.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width - 0.5
      const y = (e.clientY - rect.top) / rect.height - 0.5
      product.style.transform = `perspective(900px) rotateY(${x * 4}deg) rotateX(${-y * 3}deg)`
    })
  })
  hero.addEventListener('mouseleave', () => {
    if (raf) cancelAnimationFrame(raf)
    product.style.transform = ''
  })
}
